import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import AuthProvider from '@/auth/AuthProvider'
import { IS_DEV } from '@/common/env'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'

let LazyBffAuthProvider: ComponentType<{ children: ReactNode }> | undefined

// Unlike the inline `import.meta.env.DEV`-literal guards elsewhere in this codebase, this one
// deliberately imports the shared, mockable IS_DEV so AppAuthProvider.spec.tsx can exercise
// both branches. Verified (2026-10) via `vite build` with VITE_ENV set per the Makefile that
// this still gets dead-code-eliminated on non-dev stages — don't "fix" this to match the
// inline pattern without re-checking build/assets, since that would just break the tests.
if (IS_DEV) {
    LazyBffAuthProvider = lazy(() =>
        import('@/auth/features/bff-auth/BffAuthProvider').then((m) => ({ default: m.BffAuthProvider })),
    )
}

export interface AppAuthProviderProps {
    children: ReactNode
}

/** Picks the BFF or Amplify auth provider the same way in every top-level route tree
 *  (RootContainer, OnboardingContainer) — each tree mounts its own provider instance,
 *  so they must agree on which backend to check or a BFF login looks logged-out
 *  once the router switches trees.
 */
export function AppAuthProvider({ children }: AppAuthProviderProps) {
    const { showBffAuth, showOnboardingV2 } = useLocalFlags()
    // BFF auth has no legacy-UI login path (`LoginV2` owns the backend switch), so it
    // requires the V2 login UI to be enabled too.
    const bffAuthEnabled = showBffAuth && showOnboardingV2

    if (bffAuthEnabled && LazyBffAuthProvider) {
        return (
            <Suspense fallback={<FullScreenLoader dark />}>
                <LazyBffAuthProvider>{children}</LazyBffAuthProvider>
            </Suspense>
        )
    }

    return <AuthProvider>{children}</AuthProvider>
}
